import React, { useState, useEffect, useMemo, useRef, Suspense } from 'react';
import { Canvas, useFrame, extend } from '@react-three/fiber';
import { Stars, OrbitControls, Ring, Line as DreiLine, Html, Environment, Float, shaderMaterial } from '@react-three/drei';
import { useSpring, a } from '@react-spring/three';
import { EffectComposer, Bloom, Vignette, ChromaticAberration } from '@react-three/postprocessing';
import * as THREE from 'three';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../AppContext';
import BlackHole from './BlackHole';
import NeutronStar from './NeutronStar';
import GammaRayBurst from './GammaRayBurst';
import Supernova from './Supernova';
import Protostar from './Protostar';
import RedGiant from './RedGiant';
import WhiteDwarf from './WhiteDwarf';

// --- COMPONENTS ---

const Moonlet = ({ radius, speed, angleOffset, scale = 1 }) => {    const ref = useRef();
    useFrame((state) => {
        const t = state.clock.getElapsedTime() * speed;
        ref.current.position.x = (radius * scale) * Math.cos(t + angleOffset);
        ref.current.position.z = (radius * scale) * Math.sin(t + angleOffset);
    });
    return (
        <mesh ref={ref}>
            <sphereGeometry args={[0.06 * scale, 16, 16]} />
            <meshStandardMaterial 
                emissive="#ffffff" 
                emissiveIntensity={4} 
                toneMapped={false} 
                color="#ffffff"
            />
            <pointLight distance={0.5} intensity={2} color="white" />
        </mesh>
    );
};

const CrystallineRing = ({ radius, speed, rotation, color }) => {
    const ref = useRef();
    useFrame(() => {
        if (ref.current) ref.current.rotation.z += speed;
    });
    return (
        <group rotation={rotation}>
            <Ring ref={ref} args={[radius, radius + 0.02, 64]}>
                <meshPhysicalMaterial 
                    color={color} 
                    emissive={color} 
                    emissiveIntensity={10} 
                    toneMapped={false} 
                    transparent 
                    opacity={0.6}
                    roughness={0}
                    metalness={1}
                    side={THREE.DoubleSide}
                />
            </Ring>
        </group>
    );
};

const AnimatedLine = ({ startRef, endRef, level }) => {
    const [points, setPoints] = useState([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 0)]);
    const startVec = useMemo(() => new THREE.Vector3(), []);
    const endVec = useMemo(() => new THREE.Vector3(), []);

    useFrame(() => {
        if (startRef.current && endRef.current) {
            startRef.current.getWorldPosition(startVec);
            endRef.current.getWorldPosition(endVec);
            setPoints([startVec.clone(), endVec.clone()]);
        }
    });

    return (
        <DreiLine 
            points={points} 
            lineWidth={1} 
            color="#a5b4fc" 
            transparent 
            opacity={0.15} 
        />
    );
};

const Star = React.forwardRef(({ position, userData, isCenterStar }, ref) => {
    const { handleUserSelect } = useAppContext();
    const [isHovered, setIsHovered] = useState(false);

    const level = userData?.constellationLevel || 1;
    const sizeBonus = level >= 66 ? 0.75 : level >= 26 ? 0.3 : 0;
    const finalBaseScale = 1.2 + sizeBonus;
    const starColor = isCenterStar ? '#22d3ee' : '#818cf8'; 
    const coreSize = isCenterStar ? 0.35 : 0.25;

    const { scale } = useSpring({
        scale: isHovered ? (finalBaseScale) * 1.2 : finalBaseScale,
        config: { mass: 1, tension: 280, friction: 60 },
    });

    useFrame((state) => {
        if (ref.current) {
            const t = state.clock.getElapsedTime();
            ref.current.rotation.y = t * 0.1;
            ref.current.rotation.z = t * 0.05;
            const pulse = Math.sin(t * 2 + (userData.id.charCodeAt(0) % 10)) * 0.05;
            const currentScale = scale.get() + pulse;
            ref.current.scale.setScalar(currentScale);
        }
    });

    return (
        <group position={position}>
            <a.group ref={ref}>
                <mesh
                    onPointerOver={(e) => { e.stopPropagation(); setIsHovered(true); }}
                    onPointerOut={() => { setIsHovered(false); }}
                    visible={false}
                >
                    <sphereGeometry args={[1.5, 16, 16]} />
                </mesh>

                <mesh>
                    <sphereGeometry args={[coreSize, 32, 32]} />
                    <meshPhysicalMaterial 
                        color={starColor}
                        emissive={starColor}
                        emissiveIntensity={10}
                        roughness={0.2}
                        metalness={0.8}
                        clearcoat={1}
                        clearcoatRoughness={0.1}
                        toneMapped={false}
                    />
                    <pointLight color={starColor} intensity={3} distance={6} decay={2} />
                </mesh>

                <mesh>
                    <sphereGeometry args={[coreSize * 1.4, 32, 32]} />
                    <meshPhysicalMaterial 
                        color={starColor}
                        transparent
                        opacity={0.15}
                        roughness={0}
                        metalness={0}
                        transmission={0.5}
                        thickness={2}
                        depthWrite={false}
                    />
                </mesh>

                <group>
                    {level >= 2 && <Moonlet scale={1} radius={0.6} speed={0.8} angleOffset={0} />}
                    {level >= 11 && <Moonlet scale={1} radius={0.75} speed={-0.6} angleOffset={Math.PI} />}
                    {level >= 26 && <CrystallineRing radius={0.95} speed={0.005} rotation={[Math.PI / 3, 0, 0]} color="#c4b5fd" />}
                    {level >= 46 && <CrystallineRing radius={1.2} speed={-0.003} rotation={[-Math.PI / 4, 0, 0]} color="#f472b6" />}
                </group>
            </a.group>
        </group>
    );
});

const ShootingStar = () => {
    const ref = useRef();
    const [active, setActive] = useState(false);

    useFrame((state, delta) => {
        if (active && ref.current) {
            ref.current.position.x -= 800 * delta; 
            ref.current.position.y -= 90 * delta; 
            
            if (ref.current.position.x < -200) setActive(false); 
        } else if (!active && Math.random() < 0.01) { 
            setActive(true);
            const yStart = -20 + Math.random() * 60;
            const zStart = -100 + Math.random() * 120;
            ref.current.position.set(200, yStart, zStart); 
            
            const angle = Math.atan2(-90, -800) - Math.PI / 2;
            ref.current.rotation.z = angle; 
        }
    });

    return (
        <mesh ref={ref} visible={active}>
            <cylinderGeometry args={[0, 0.08, 12, 8]} /> 
            <meshBasicMaterial 
                color={new THREE.Color(0.5, 0.8, 1).multiplyScalar(5)} 
                transparent 
                opacity={0.8} 
                blending={THREE.AdditiveBlending} 
                toneMapped={false} 
            />
        </mesh>
    );
};

const ResponsiveCamera = () => {
    useFrame(({ camera, viewport }) => {
        const isMobile = viewport.aspect < 1;
        const targetFov = isMobile ? 85 : 60;
        camera.fov = THREE.MathUtils.lerp(camera.fov, targetFov, 0.1);
        camera.updateProjectionMatrix();
    });
    return null;
};

const BackgroundConstellations = () => {
    const groupRef = useRef();
    
    // Define constellations data with larger stars and patterns
    const constellationData = useMemo(() => {
        const R = 180; // Radius for background constellations (pushed further back)
        
        const getPoint = (phi, theta) => {
            return new THREE.Vector3().setFromSphericalCoords(R, phi, theta);
        };

        return [
            {
                name: 'The Serpent',
                color: '#a5b4fc', // Indigo
                points: [
                    getPoint(1.0, 0.5),
                    getPoint(1.1, 0.7),
                    getPoint(1.0, 0.9),
                    getPoint(0.9, 1.1),
                    getPoint(1.0, 1.3)
                ]
            },
            {
                name: 'The Guardian',
                color: '#fbbf24', // Amber
                points: [
                    getPoint(2.0, 3.5),
                    getPoint(2.2, 3.6),
                    getPoint(2.1, 3.8),
                    getPoint(1.9, 3.7),
                    getPoint(2.0, 3.5)
                ]
            },
            {
                name: 'The Arrow',
                color: '#22d3ee', // Cyan
                points: [
                    getPoint(1.5, 4.5), // Tip
                    getPoint(1.6, 4.4),
                    getPoint(1.7, 4.3), // Shaft
                    getPoint(1.75, 4.2), // Fin
                    getPoint(1.7, 4.3),
                    getPoint(1.75, 4.4)  // Fin
                ]
            },
            {
                name: 'The Crown',
                color: '#f472b6', // Pink
                points: [
                    getPoint(0.5, 2.0),
                    getPoint(0.6, 2.1),
                    getPoint(0.5, 2.2),
                    getPoint(0.6, 2.3),
                    getPoint(0.5, 2.4)
                ]
            },
            {
                name: 'The Chalice',
                color: '#c084fc', // Purple
                points: [
                     getPoint(2.5, 1.0),
                     getPoint(2.6, 1.1),
                     getPoint(2.6, 1.2),
                     getPoint(2.5, 1.3),
                     getPoint(2.5, 1.0), // Cup
                     getPoint(2.7, 1.15) // Stem
                ]
            }
        ];
    }, []);

    // Slow rotation for the distant sky to create parallax with foreground
    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * 0.005; // Even slower drift
            // Gentle wobble
            groupRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.02) * 0.02;
        }
    });

    return (
        <group ref={groupRef}>
            {constellationData.map((c, i) => (
                <group key={i}>
                    {/* Constellation Lines */}
                    <DreiLine 
                        points={c.points} 
                        color={c.color} 
                        opacity={0.08} 
                        transparent 
                        lineWidth={1} 
                    />
                    {/* Large Stars at Vertices */}
                    {c.points.map((p, j) => (
                        <mesh key={j} position={p}>
                            <sphereGeometry args={[0.25, 8, 8]} />
                            <meshBasicMaterial 
                                color={c.color} 
                                toneMapped={false} 
                            />
                        </mesh>
                    ))}
                </group>
            ))}
        </group>
    );
};

const MockConstellation = () => {
    const { t } = useTranslation();
    const starRefs = useRef(new Map());

        const mockProfiles = useMemo(() => {

            const centerStar = { id: 'center-star', displayName: t('immersiveIntro.mockProfiles.you'), constellationLevel: 1 };

            const connections = [

                { id: 'proto-1', displayName: 'Protostar', constellationLevel: 5 }, // Protostar

                { id: 'conn-2', displayName: t('immersiveIntro.mockProfiles.respectedVoice'), constellationLevel: 25 }, // Main Sequence

                { id: 'red-giant-1', displayName: 'Red Giant', constellationLevel: 45 }, // Red Giant

                { id: 'white-dwarf-1', displayName: 'White Dwarf', constellationLevel: 55 }, // White Dwarf

                { id: 'supernova-1', displayName: 'Nova', constellationLevel: 65 }, // Supernova

                { id: 'grb-1', displayName: 'Supernova', constellationLevel: 75 }, // Gamma Ray Burst

                { id: 'neutron-star-1', displayName: 'Pulsar', constellationLevel: 85 }, // Neutron Star

                { id: 'black-hole-1', displayName: t('immersiveIntro.mockProfiles.blackHole'), constellationLevel: 99 },

            ];

            return [centerStar, ...connections];

        }, [t]);
            
                mockProfiles.forEach(profile => {
                    if (!starRefs.current.has(profile.id)) {
                        starRefs.current.set(profile.id, React.createRef());
                    }
                });
            
                const starPositions = useMemo(() => {
                    const posMap = new Map();
                    const centerUser = mockProfiles[0];
                    const otherUsers = mockProfiles.slice(1);
                    posMap.set(centerUser.id, [0, 0, 0]);
            
                    const radius = 6;
                    otherUsers.forEach((profile, index) => {
            // Check if this is the Black Hole or Neutron Star profile
            if (profile.id === 'black-hole-1') {
                posMap.set(profile.id, [0, -10, 0]); 
            } else if (profile.id === 'neutron-star-1') {
                posMap.set(profile.id, [-8, 5, -2]); 
            } else if (profile.id === 'grb-1') {
                posMap.set(profile.id, [8, 6, -3]); 
            } else if (profile.id === 'supernova-1') {
                posMap.set(profile.id, [-5, -6, 2]); 
            } else if (profile.id === 'red-giant-1') {
                posMap.set(profile.id, [6, -4, 4]); // Bottom right foreground
            } else if (profile.id === 'white-dwarf-1') {
                posMap.set(profile.id, [4, 8, 1]); // Top right
            } else if (profile.id === 'proto-1') {
                posMap.set(profile.id, [-6, 2, 5]); // Left foreground
            } else {
                const numPoints = otherUsers.length;                                        // Adjust the index for regular stars to account for special bodies
                                        const effectiveIndex = index; 
                                        const y = 1 - (effectiveIndex / (numPoints - 1)) * 2; 
                                        const radiusAtY = Math.sqrt(1 - y * y);
                                        const phi = (effectiveIndex + 1) * Math.PI * (3 - Math.sqrt(5));
                                        const x = Math.cos(phi) * radiusAtY;
                                        const z = Math.sin(phi) * radiusAtY;
                                        posMap.set(profile.id, [x * radius, y * radius, z * radius]);
                                    }
                                });
                                return posMap;
                            }, [mockProfiles]);

    const centerUserId = mockProfiles[0].id;

    return (
        <>
            <color attach="background" args={['#000000']} />
            <Stars radius={150} depth={60} count={40000} factor={4} saturation={0} fade speed={0.5} />
            <Stars radius={150} depth={60} count={5000} factor={9} saturation={0} fade speed={0.6} />
            <Stars radius={150} depth={60} count={800} factor={14} saturation={1} fade speed={0.7} />
            
            <ambientLight intensity={0.1} />
            <pointLight position={[10, 10, 10]} intensity={1} color="#818cf8" />

            <Environment preset="city" />

            <BackgroundConstellations />

            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />
            <ShootingStar />

            <EffectComposer multisampling={0}>
                <Bloom luminanceThreshold={0.2} luminanceSmoothing={0.9} height={300} intensity={2.5} />
                <ChromaticAberration offset={[0.0005, 0.0005]} />
                <Vignette eskil={false} offset={0.1} darkness={1.1} />
            </EffectComposer>

            <Float speed={1} rotationIntensity={0.1} floatIntensity={0.2}>
                {mockProfiles.map(profile => {
                    if (profile.id === 'black-hole-1') {
                        return (
                            <group 
                                ref={starRefs.current.get(profile.id)}
                                key={profile.id}
                                position={starPositions.get(profile.id)}
                            >
                                <BlackHole scale={2} />
                            </group>
                        );
                    } else if (profile.id === 'neutron-star-1') {
                        return (
                            <group 
                                ref={starRefs.current.get(profile.id)}
                                key={profile.id}
                                position={starPositions.get(profile.id)}
                            >
                                <NeutronStar scale={1.5} />
                            </group>
                        );
                    } else if (profile.id === 'grb-1') {
                        return (
                            <group 
                                ref={starRefs.current.get(profile.id)}
                                key={profile.id}
                                position={starPositions.get(profile.id)}
                            >
                                <GammaRayBurst scale={1.2} />
                            </group>
                        );
                    } else if (profile.id === 'supernova-1') {
                        return (
                            <group 
                                ref={starRefs.current.get(profile.id)}
                                key={profile.id}
                                position={starPositions.get(profile.id)}
                            >
                                <Supernova scale={1.5} />
                            </group>
                        );
                    } else if (profile.constellationLevel < 10 && profile.id !== centerUserId) {
                        return (
                            <group ref={starRefs.current.get(profile.id)} key={profile.id} position={starPositions.get(profile.id)}>
                                <Protostar scale={1} />
                            </group>
                        );
                    } else if (profile.constellationLevel >= 30 && profile.constellationLevel < 50) {
                        return (
                            <group ref={starRefs.current.get(profile.id)} key={profile.id} position={starPositions.get(profile.id)}>
                                <RedGiant scale={1.2} />
                            </group>
                        );
                    } else if (profile.constellationLevel >= 50 && profile.constellationLevel < 60) {
                        return (
                            <group ref={starRefs.current.get(profile.id)} key={profile.id} position={starPositions.get(profile.id)}>
                                <WhiteDwarf scale={0.8} />
                            </group>
                        );
                    } else {
                        return (
                            <Star
                                ref={starRefs.current.get(profile.id)}
                                key={profile.id}
                                position={starPositions.get(profile.id)}
                                userData={profile}
                                isCenterStar={profile.id === centerUserId}
                            />
                        );
                    }
                })}

                {mockProfiles.slice(1).map(profile => {
                    const centerRef = starRefs.current.get(centerUserId);
                    const targetRef = starRefs.current.get(profile.id);
                    if (centerRef && targetRef) {
                        // Ensure BlackHole also gets connected to the center star
                        return <AnimatedLine key={`line-${profile.id}`} startRef={centerRef} endRef={targetRef} level={1} />;
                    }
                    return null;
                })}
            </Float>
        </>
    );
};

function ImmersiveIntro({ 
    onEnter, 
    onShowSignIn, 
    onSignIn, 
    onBack, 
    introState,
    signInWithApple,
    signInWithEmail,
    signUpWithEmail
}) {
    const { LucideIcons } = useAppContext();
    const { t } = useTranslation();
    const [showUI, setShowUI] = useState(false);
    const [isCanvasReady, setIsCanvasReady] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSignUp, setIsSignUp] = useState(false);
    const [error, setError] = useState('');
    
    const [honeypot, setHoneypot] = useState('');
    const formLoadTime = useRef(Date.now());

    // Music State
    const [isMusicPlaying, setIsMusicPlaying] = useState(false);
    const audioRef = useRef(null);

    const toggleMusic = () => {
        if (audioRef.current) {
            if (isMusicPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.volume = 0.3;
                audioRef.current.play().catch(e => console.log("Audio play failed:", e));
            }
            setIsMusicPlaying(!isMusicPlaying);
        }
    };

    useEffect(() => {
        const uiTimer = setTimeout(() => setShowUI(true), 500);
        const canvasTimer = setTimeout(() => setIsCanvasReady(true), 100);
        
        return () => {
            clearTimeout(uiTimer);
            clearTimeout(canvasTimer);
        };
    }, [introState]);

    const handleGoogleSignIn = async () => {
        try {
            await onSignIn();
        } catch (error) {
            console.error("Google Sign-in failed during intro:", error);
            setError(error.message);
        }
    };

    const handleAppleSignIn = async () => {
        try {
            await signInWithApple();
        } catch (error) {
            console.error("Apple Sign-in failed during intro:", error);
            setError(error.message);
        }
    };

    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const validatePassword = (password) => {
        if (password.length < 8) return "Password must be at least 8 characters long.";
        if (!/[A-Z]/.test(password)) return "Password must contain at least one uppercase letter.";
        if (!/[0-9]/.test(password)) return "Password must contain at least one number.";
        if (!/[!@#$%^&*]/.test(password)) return "Password must contain at least one special character (!@#$%^&*).";
        return null;
    };

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setError('');

        if (honeypot) {
            console.warn("Bot detected via honeypot.");
            return;
        }

        if (Date.now() - formLoadTime.current < 2000) {
             console.warn("Bot detected via rapid submission.");
             setError("Please wait a moment before submitting.");
             return;
        }

        if (!validateEmail(email)) {
            setError("Please enter a valid email address.");
            return;
        }

        if (isSignUp) {
            const passwordError = validatePassword(password);
            if (passwordError) {
                setError(passwordError);
                return;
            }
        }

        try {
            if (isSignUp) {
                await signUpWithEmail(email, password);
            } else {
                await signInWithEmail(email, password);
            }
        } catch (error) {
            console.error("Email auth failed:", error);
            setError(error.message);
        }
    };

    const renderContent = () => {
        switch (introState) {
            case 'SIGN_IN':
                return (
                    <>
                        <h1 className="intro-title">{isSignUp ? t('immersiveIntro.signUp.title') : t('immersiveIntro.signIn.title')}</h1>
                        <p className="intro-subtitle">{isSignUp ? t('immersiveIntro.signUp.subtitle') : t('immersiveIntro.signIn.subtitle')}</p>
                        
                        <div className="space-y-3 w-full max-w-xs mx-auto">
                            <button onClick={handleGoogleSignIn} className="intro-button google w-full">
                                <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path><path fill="none" d="M0 0h48v48H0z"></path></svg>
                                {t('immersiveIntro.signIn.googleButton')}
                            </button>
                            <button onClick={handleAppleSignIn} className="intro-button apple w-full">
                                <LucideIcons.Apple size={22} /> {t('immersiveIntro.signIn.appleButton')}
                            </button>
                        </div>

                        <div className="relative flex py-5 items-center w-full max-w-xs mx-auto">
                            <div className="flex-grow border-t border-gray-600"></div>
                            <span className="flex-shrink mx-4 text-gray-300 text-sm font-medium tracking-wide">{t('immersiveIntro.signIn.or')}</span>
                            <div className="flex-grow border-t border-gray-600"></div>
                        </div>

                        <form onSubmit={handleEmailAuth} className="space-y-6 w-full max-w-xs mx-auto">
                            <input 
                                type="text" 
                                name="website" 
                                value={honeypot}
                                onChange={(e) => setHoneypot(e.target.value)}
                                style={{ display: 'none' }}
                                autoComplete="off"
                                tabIndex="-1"
                            />

                            <input 
                                type="email" 
                                placeholder={t('immersiveIntro.signIn.emailPlaceholder')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="intro-input w-full"
                            />
                            <input 
                                type="password" 
                                placeholder={t('immersiveIntro.signIn.passwordPlaceholder')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="intro-input w-full"
                            />
                            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
                            <button type="submit" className="intro-button w-full">
                                {isSignUp ? t('immersiveIntro.signUp.button') : t('immersiveIntro.signIn.button')}
                            </button>
                        </form>
                        
                        <button onClick={() => { setIsSignUp(!isSignUp); setError(''); }} className="text-sm text-gray-200 hover:text-white transition-colors duration-300 mt-6 font-medium tracking-wide">
                            {isSignUp ? t('immersiveIntro.signUp.toggle') : t('immersiveIntro.signIn.toggle')}
                        </button>

                        <button onClick={onBack} className="intro-back-button mt-6">{t('immersiveIntro.signIn.goBack')}</button>
                        
                        <p className="text-xs text-gray-300 mt-8 text-center max-w-xs mx-auto leading-relaxed opacity-80 hover:opacity-100 transition-opacity">
                            This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-cyan-300 transition-colors">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-cyan-300 transition-colors">Terms of Service</a> apply.
                        </p>
                    </>
                );
            case 'WELCOME_BACK':
                return (
                    <>
                        <h1 className="intro-title">{t('immersiveIntro.welcomeBack.title')}</h1>
                        <p className="intro-subtitle">{t('immersiveIntro.welcomeBack.subtitle')}</p>
                        <button onClick={onEnter} className="intro-button">{t('immersiveIntro.welcomeBack.button')}</button>
                    </>
                );
            case 'LANDING':
            default:
                return (
                    <>
                        <h1 className="intro-title">{t('immersiveIntro.landing.title')}</h1>
                        <p className="intro-subtitle">{t('immersiveIntro.landing.subtitle')}</p>
                        <button onClick={onShowSignIn} className="intro-button">{t('immersiveIntro.landing.button')}</button>
                    </>
                );
        }
    };

    return (
        <div className="intro-container" style={{ zIndex: 9999 }}>
            {/* Audio Element */}
            <audio 
                ref={audioRef} 
                src="https://github.com/Jelani0/WhispersOfHarmony/raw/refs/heads/main/melodyloops-touch-of-hope.mp3" 
                loop 
            />
            
            {/* Music Toggle - Top Right */}
            <div className="absolute top-6 right-6 z-50">
                <button 
                    onClick={toggleMusic}
                    className={`p-3 rounded-full backdrop-blur-md border border-white/10 transition-all duration-500 ${isMusicPlaying ? 'bg-indigo-500/20 text-indigo-300 shadow-[0_0_15px_rgba(99,102,241,0.5)]' : 'bg-black/20 text-gray-400 hover:bg-white/10 hover:text-white'}`}
                >
                    {isMusicPlaying ? (
                        <div className="flex items-center gap-2">
                            <LucideIcons.Volume2 size={20} />
                            {/* Simple visualizer bars */}
                            <div className="flex gap-0.5 items-end h-3">
                                <span className="w-0.5 h-full bg-indigo-300 animate-[pulse_0.6s_ease-in-out_infinite]" />
                                <span className="w-0.5 h-2/3 bg-indigo-300 animate-[pulse_0.8s_ease-in-out_infinite]" />
                                <span className="w-0.5 h-full bg-indigo-300 animate-[pulse_1s_ease-in-out_infinite]" />
                            </div>
                        </div>
                    ) : (
                        <LucideIcons.VolumeX size={20} />
                    )}
                </button>
            </div>

            {isCanvasReady && (
                <Canvas camera={{ position: [0, 0, 15], fov: 60, far: 1000 }} dpr={[1, 1.5]} gl={{ antialias: false, toneMapping: THREE.NoToneMapping }}>
                    <Suspense fallback={<Html center><div className="text-white text-sm opacity-50">Loading Universe...</div></Html>}>
                        <ResponsiveCamera />
                        <MockConstellation />
                        <OrbitControls enableZoom={true} enablePan={false} minDistance={8} maxDistance={40} autoRotate autoRotateSpeed={0.6} />
                    </Suspense>
                </Canvas>
            )}
            <div className={`intro-ui-content ${showUI ? 'visible' : ''}`}>
                {renderContent()}
            </div>
        </div>
    );
};

export default ImmersiveIntro;